import { Button, Card, Form, Input, Space, Switch, Tooltip } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTrackEvents } from './TrackEvents.hook';
import TextArea from 'antd/es/input/TextArea';
import { useSelector } from 'react-redux';
import { clearState, selectAllSettings } from '../../../../modules/settings/store/slice';
import { useDispatch } from 'react-redux';
import {
  doCreateSetting,
  doDeleteSetting,
  doGetSettings,
  doUpdateSetting,
} from '../../../../modules/settings/store/thunk';
import { ReactSVG } from 'react-svg';
import { SendTestMail } from '../Integrations/SendTestMail';
import { doSendTestMail } from '../../../../modules/sendgrid/store/thunk';
import { correctVariablesRule } from './EventVariable.validation';
import { ROLES } from '../../../../constants';

// simple inline component to show a switch with tooltip
const SwitchWithTooltip = ({ event, onChange }) => {
  return (
    <Tooltip title={`Add a custom title and body for the email triggered by the ${event.name} event`} placement="right">
      <Switch onChange={() => onChange(event.index)} checked={event.on} />
    </Tooltip>
  );
};

const VariableUseTooltip = ({ children }) => (
  <Tooltip
    placement="right"
    title="Use variables with curly brackets: Both {{ company }} or {{ location }} are valid and will be substituted in final content."
  >
    {children}
  </Tooltip>
);

export const Events = ({ locationId, companyId }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);

  const existing = useSelector((state) => selectAllSettings(state));

  const objectProps = useMemo(() => ({
    companyId: companyId || undefined,
    locationId: locationId || undefined,
  }));
  const userRole = useSelector((state) => state.auth.user.role);

  useEffect(() => {
    if (userRole === ROLES.ADMIN || companyId) {
      dispatch(doGetSettings({ category: 'tnt-events', ...objectProps }));
    }
    return () => dispatch(clearState()); // explicitly clear state on unmount
  }, [companyId, locationId]);

  const { events, toggleEvent, updateEvent, initialize } = useTrackEvents(existing);

  useEffect(() => {
    if (existing) {
      initialize(existing);
    }
  }, [existing]);

  const cancel = () => {
    if (existing) {
      initialize(existing);
    }
  };
  const submit = async () => {
    setIsSaving(true);
    // construct param objects from input

    const updates = events.map((s) => ({
      id: existing.find((e) => s.key === e.keyword)?.id || null,
      keyword: s.key,
      value: s.on ? 'true' : 'false',
      extraData: {
        subject: s.subject,
        body: s.body,
        closing: s.closing,
      },
      group: 'TNT_EVENTS',
      ...objectProps,
    }));

    // send updates or creates to backend depending on their existance
    await Promise.all(
      updates.map(async (u) => {
        if (u.id && u.value) {
          // if it existed and has a value => update it
          await dispatch(doUpdateSetting(u));
        } else if (u.id && !u.value) {
          // if it existed and does not have a value => delete it
          await dispatch(doDeleteSetting(u));
        } else if (u.value) {
          // if it didn't exist => create it
          await dispatch(doCreateSetting(u));
        }
      }),
    );
    await dispatch(doGetSettings({ category: 'tnt-events', ...objectProps }));
    setIsSaving(false);
  };

  const sendTest = async (index) => {
    const body = {
      ...events.find((e) => e.index === index),
      status: events.find((e) => e.index === index).key,
    };

    dispatch(doSendTestMail(body));
  };

  return !locationId && !companyId ? (
    <h4>Please select a company or location first</h4>
  ) : (
    <div>
      <h4>Events</h4>

      <Form form={form} onFinish={submit} layout="vertical" requiredMark={false} colon={false}>
        <Space direction="vertical" size={16}>
          {events &&
            events.map((e) => (
              <Card
                title={e.name}
                extra={<SwitchWithTooltip event={e} onChange={toggleEvent} />}
                style={{ width: 400, border: 'none' }}
                key={e.name}
              >
                {e.on && (
                  <>
                    <VariableUseTooltip>
                      <Form.Item label="Subject" name={[e.index, 'subject']} value={e.subject} initialValue={e.subject}>
                        <Input
                          size="large"
                          onBlur={(b) => updateEvent({ index: e.index, field: 'subject', value: b.target.value })}
                          rules={[correctVariablesRule]}
                        />
                      </Form.Item>
                    </VariableUseTooltip>
                    <VariableUseTooltip>
                      <Form.Item
                        label="Body"
                        name={[e.index, 'body']}
                        initialValue={e.body}
                        rules={[correctVariablesRule]}
                      >
                        <TextArea
                          size="large"
                          autoSize={{ minRows: 6, maxRows: 10 }}
                          onBlur={(b) => updateEvent({ index: e.index, field: 'body', value: b.target.value })}
                        />
                      </Form.Item>
                    </VariableUseTooltip>
                    <VariableUseTooltip>
                      <Form.Item
                        label="Closing"
                        name={[e.index, 'closing']}
                        value={e.closing}
                        initialValue={e.closing}
                        rules={[correctVariablesRule]}
                      >
                        <TextArea
                          size="large"
                          autoSize={{ minRows: 4, maxRows: 6 }}
                          onBlur={(b) => updateEvent({ index: e.index, field: 'closing', value: b.target.value })}
                        />
                      </Form.Item>
                    </VariableUseTooltip>
                    <SendTestMail size="small" style={{ marginLeft: 0 }} send={() => sendTest(e.index)} />
                  </>
                )}
              </Card>
            ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button key="cancel" size="small" onClick={cancel}>
              reset
            </Button>
            <Button key="confirm" type="primary" size="small" htmlType="submit" loading={isSaving}>
              {existing ? 'Update' : 'Add'} event settings
              <ReactSVG className="icon" src="/icons/done.svg" />
            </Button>
          </div>
        </Space>
      </Form>
    </div>
  );
};
